import { t } from '../i18n';

/**
 * 派件司机手机号 / 证件号唯一性冲突提示（后端 AccountDeliveryDriverApplication.validateDriverUniqueness）
 */
export const DRIVER_CONFLICT_MESSAGE_KEYS = [
    'errors.business.account.deliveryDriver.phoneRegisteredAsRole',
    'errors.business.account.deliveryDriver.phoneRegisteredAsDeliveryDriver',
    'errors.business.account.deliveryDriver.idDocumentNumberBoundInDsp',
];

export const isDriverConflictMessageKey = (messageKey?: string | null): boolean =>
    !!messageKey && DRIVER_CONFLICT_MESSAGE_KEYS.includes(messageKey);

/**
 * 后端 details 结构，字段均按可空处理
 */
export interface DriverConflictDetails {
    roleKey?: string | null;
    dspName?: string | null;
    dspContactPhone?: string | null;
    locations?: Array<{ hubName?: string | null; warehouseName?: string | null }> | null;
}

/**
 * 位置展示格式：Hub · Warehouse · DSP。DSP 关联多个仓库时只取第一个（后端已按绑定先后排序），缺失的层级省略
 */
export const formatDspPath = (details?: DriverConflictDetails | null): string => {
    if (!details?.dspName) {
        return '';
    }
    const first = details.locations?.[0];
    return [first?.hubName, first?.warehouseName, details.dspName].filter(Boolean).join(' · ');
};

/**
 * 构造冲突文案的插值参数：roleName（角色名称）；位置不在正文中，弹窗用信息框、导入用句末后缀展示
 */
export const buildDriverConflictInterpolation = (details?: DriverConflictDetails | null): Record<string, string> => {
    const roleKey = details?.roleKey;
    return {
        roleName: roleKey ? t(`enum.role.${roleKey}`, { defaultValue: roleKey }) : '',
    };
};

export interface DriverConflictLocationRow {
    labelKey: string;
    value: string;
    emphasis?: boolean;
}

/**
 * 弹窗信息框的行：Hub / 仓库 / DSP，缺失的层级不展示，DSP 加粗；DSP 关联多个仓库时只取第一个；无 DSP 名称时返回空数组
 */
export const buildDriverConflictLocationRows = (details?: DriverConflictDetails | null): DriverConflictLocationRow[] => {
    if (!details?.dspName) {
        return [];
    }
    const first = details.locations?.[0];
    const labelPrefix = 'errors.business.account.deliveryDriver.conflictLocation';
    return [
        { labelKey: `${labelPrefix}.hub`, value: first?.hubName ?? '' },
        { labelKey: `${labelPrefix}.warehouse`, value: first?.warehouseName ?? '' },
        { labelKey: `${labelPrefix}.dsp`, value: details.dspName, emphasis: true },
    ].filter(row => row.value);
};

/**
 * 批量导入失败原因（表格纯文本）：去掉加粗标记，有位置时句末补「（Hub · Warehouse · DSP）」
 */
export const toDriverConflictPlainText = (message: string, details?: DriverConflictDetails | null): string => {
    const text = stripBoldTags(message);
    const dspPath = formatDspPath(details);
    return dspPath ? `${text}${t('errors.business.account.deliveryDriver.conflictLocation.suffix', { dspPath })}` : text;
};

/**
 * 冲突文案中用 <b></b> 标记需加粗的名称，弹窗通过 Trans 渲染；纯文本场景（批量导入失败原因）去掉标记
 */
export const stripBoldTags = (text: string): string => text.replace(/<\/?b>/g, '');

/**
 * 解析后端返回的 details（GraphQL 中为 JSON 字符串或对象），解析失败返回 undefined
 */
export const parseDriverConflictDetails = (details?: string | Record<string, any> | null): DriverConflictDetails | undefined => {
    if (!details) {
        return undefined;
    }
    if (typeof details !== 'string') {
        return details as DriverConflictDetails;
    }
    try {
        return JSON.parse(details) as DriverConflictDetails;
    } catch (e) {
        console.warn('Failed to parse driver conflict details:', details, e);
        return undefined;
    }
};

/**
 * 表单失焦时是否需要查询唯一性：值非空、与打开表单时的原值不同（编辑时自己的号码不查）、与本次聚焦时的值不同（未改动不重复查询），且不是离职操作
 */
export const shouldCheckDriverUniqueness = (params: {
    value?: string | null;
    originalValue?: string | null;
    focusValue?: string | null;
    isResigning?: boolean;
}): boolean => {
    const value = params.value?.trim() ?? '';
    if (!value || params.isResigning) {
        return false;
    }
    return value !== (params.originalValue?.trim() ?? '') && value !== (params.focusValue?.trim() ?? '');
};

export type DriverUniquenessField = 'phone' | 'idDocumentNumber';

/**
 * 失焦查询结果守卫：同一字段只认最近一次查询（避免先发后到的旧结果弹窗）；
 * 提交或关闭抽屉时 invalidateAll，此前发出的查询结果全部作废（提交以后端返回为准，关闭后不再弹窗）
 */
export const createUniquenessCheckGuard = () => {
    const seqs: Record<DriverUniquenessField, number> = { phone: 0, idDocumentNumber: 0 };
    return {
        start: (field: DriverUniquenessField): number => ++seqs[field],
        invalidateAll: (): void => {
            seqs.phone++;
            seqs.idDocumentNumber++;
        },
        isLatest: (field: DriverUniquenessField, token: number): boolean => seqs[field] === token,
    };
};

export type UniquenessCheckGuard = ReturnType<typeof createUniquenessCheckGuard>;

/**
 * 等待本次鼠标点击处理完：鼠标按下时输入框即失焦，抬起后才触发 click（确认 / 取消 / 关闭 / 遮罩），
 * 失焦时若立即查询，结果可能赶在点击生效前返回并弹窗。
 * wait()：未按下时立即 resolve（如 Tab 离开）；按下时等抬起后再等一个宏任务，此时 click 已处理完
 */
export const createPointerReleaseWaiter = (target: Document = document) => {
    let pressed = false;
    let waiters: Array<() => void> = [];
    const onDown = () => {
        pressed = true;
    };
    const onUp = () => {
        pressed = false;
        const pending = waiters;
        waiters = [];
        setTimeout(() => pending.forEach(resolve => resolve()), 0);
    };
    target.addEventListener('pointerdown', onDown, true);
    target.addEventListener('pointerup', onUp, true);
    target.addEventListener('pointercancel', onUp, true);
    return {
        wait: (): Promise<void> => (pressed ? new Promise<void>(resolve => waiters.push(resolve)) : Promise.resolve()),
        dispose: (): void => {
            target.removeEventListener('pointerdown', onDown, true);
            target.removeEventListener('pointerup', onUp, true);
            target.removeEventListener('pointercancel', onUp, true);
            waiters = [];
        },
    };
};

/**
 * 冲突正文的翻译 key（弹窗与批量导入失败原因共用）：冲突 key 为 { message, tip } 结构取 .message，
 * 后端返回了 DSP 联系电话且有对应整句时取 .messageWithDspPhone（「需联系所属 DSP（电话 xxx）…」）；
 * 后端降级返回的普通 key（如 idDocumentNumberExists，冲突司机的 DSP 记录缺失时）没有 .message，取 key 本身
 */
export const resolveConflictMessageI18nKey = (messageKey: string, details?: DriverConflictDetails | null): string => {
    if (details?.dspContactPhone) {
        const withPhoneI18nKey = `${messageKey}.messageWithDspPhone`;
        if (t(withPhoneI18nKey) !== withPhoneI18nKey) {
            return withPhoneI18nKey;
        }
    }
    const messageI18nKey = `${messageKey}.message`;
    return t(messageI18nKey) !== messageI18nKey ? messageI18nKey : messageKey;
};

/**
 * 手机号失焦校验的比较值：区号 + 号码，只改区号也视为变动（区号影响位数校验）
 */
export const buildPhoneCompareValue = (countryCode?: number | string | null, phoneNumber?: string | null): string => {
    const phone = phoneNumber?.trim() ?? '';
    return phone ? `${countryCode ?? ''}-${phone}` : '';
};
